import React, { useState, useEffect } from 'react';
import { 
    Key, Plus, Search, SquarePen, Trash2, X, AlertTriangle, Save 
} from 'lucide-react'; // Íconos de Lucide React

// Datos fijos de los roles (solo se usan si no hay datos guardados localmente)
const ROLES_MOCK_DATA = [
    { id: 1, nombre: 'Médico de Base', descripcion: 'Médico con responsabilidad sobre residentes.', nivel: 'Alto' },
    { id: 2, nombre: 'R1', descripcion: 'Residente de primer año.', nivel: 'Residente' },
    { id: 3, nombre: 'R2', descripcion: 'Residente de segundo año.', nivel: 'Residente' },
    { id: 4, nombre: 'R3', descripcion: 'Residente de tercer año.', nivel: 'Residente' },
    { id: 5, nombre: 'Jefa de Enfermería', descripcion: 'Líder del personal de enfermería.', nivel: 'Medio' },
    { id: 6, nombre: 'Enfermera General', descripcion: 'Personal de enfermería con funciones generales.', nivel: 'Bajo' },
    { id: 7, nombre: 'Enfermera Auxiliar', descripcion: 'Personal de apoyo en enfermería.', nivel: 'Bajo' },
];

// Usaremos un identificador único para el almacenamiento local
const LOCAL_STORAGE_KEY = 'cleth_roles_data';

const Roles = () => {
    // Estado principal para la lista de roles
    // Nota: El uso de localStorage es temporal. En una aplicación real, se usaría Firestore.
    const [roles, setRoles] = useState(() => {
        const localData = localStorage.getItem(LOCAL_STORAGE_KEY);
        return localData ? JSON.parse(localData) : ROLES_MOCK_DATA;
    });
    
    // Estados para la funcionalidad de BÚSQUEDA
    const [searchTerm, setSearchTerm] = useState('');

    // Estados para el Modal de Crear/Editar
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formData, setFormData] = useState({ id: null, nombre: '', descripcion: '', nivel: '' });
    const [isEditing, setIsEditing] = useState(false);

    // Estados para el Modal de CONFIRMACIÓN de Eliminación
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [roleToDelete, setRoleToDelete] = useState(null);


    // 2. Efecto para GUARDAR los datos en localStorage cada vez que 'roles' cambia
    useEffect(() => {
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(roles));
    }, [roles]); 

    // **********************************************
    // LÓGICA DE BÚSQUEDA Y FILTRADO
    // **********************************************
    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    // Lista de roles filtrados (se recalculan cada vez que 'roles' o 'searchTerm' cambian)
    const filteredRoles = roles.filter(rol => {
        const term = searchTerm.toLowerCase();
        return (
            rol.nombre.toLowerCase().includes(term) ||
            rol.descripcion.toLowerCase().includes(term) ||
            rol.nivel.toLowerCase().includes(term)
        );
    });

    // **********************************************
    // LÓGICA DE MANEJO DE FORMULARIO (MODAL)
    // **********************************************
    
    const openModalForNew = () => {
        setFormData({ id: null, nombre: '', descripcion: '', nivel: '' });
        setIsEditing(false);
        setIsModalOpen(true);
    };

    const openModalForEdit = (rol) => {
        setFormData(rol);
        setIsEditing(true);
        setIsModalOpen(true);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        if (isEditing) {
            // Actualizar
            setRoles(prevRoles => prevRoles.map(rol => (rol.id === formData.id ? formData : rol)));
        } else {
            // Crear
            const newRol = { ...formData, id: Date.now() }; 
            setRoles(prevRoles => [...prevRoles, newRol]);
        }

        setIsModalOpen(false);
        setFormData({ id: null, nombre: '', descripcion: '', nivel: '' });
    };
    
    // **********************************************
    // FUNCIONES CRUD BÁSICAS Y CONFIRMACIÓN
    // **********************************************

    // 1. Abre el modal de confirmación
    const handleDelete = (rol) => {
        setRoleToDelete(rol);
        setIsConfirmModalOpen(true);
    };
    
    // 2. Ejecuta la eliminación
    const confirmDelete = () => {
        if (roleToDelete) {
            setRoles(prevRoles => prevRoles.filter(rol => rol.id !== roleToDelete.id));
        }
        setIsConfirmModalOpen(false);
        setRoleToDelete(null);
    };

    // Función para cerrar cualquier modal
    const closeModal = () => {
        setIsModalOpen(false);
        setIsConfirmModalOpen(false);
        setRoleToDelete(null);
        setFormData({ id: null, nombre: '', descripcion: '', nivel: '' });
    };


    return (
        <div className="p-4 sm:p-8 bg-gray-50 min-h-screen">
            <header className="mb-6 flex flex-col md:flex-row justify-between items-start md:items-center">
                <h1 className="text-3xl font-extrabold text-indigo-700 mb-4 md:mb-0 flex items-center">
                    <Key className="w-8 h-8 mr-2 text-indigo-500" /> Gestión de Roles
                </h1>
                <button 
                    onClick={openModalForNew}
                    className="flex items-center bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-6 rounded-xl transition duration-150 shadow-md transform hover:scale-[1.02]"
                >
                    <Plus className="w-5 h-5 mr-2" /> Nuevo Rol
                </button>
            </header>
            
            {/* BARRA DE BÚSQUEDA */}
            <div className="mb-6 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                    type="text"
                    placeholder="Buscar por rol, descripción o nivel..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className="w-full p-3 pl-10 border-2 border-gray-300 rounded-xl shadow-sm focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition duration-150"
                />
            </div>


            {/* TABLA DE ROLES */}
            <div className="bg-white shadow-xl rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-indigo-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-indigo-700 uppercase tracking-wider">Rol</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-indigo-700 uppercase tracking-wider">Descripción</th>
                                <th className="px-6 py-3 text-left text-xs font-semibold text-indigo-700 uppercase tracking-wider">Nivel</th>
                                <th className="px-6 py-3 text-center text-xs font-semibold text-indigo-700 uppercase tracking-wider">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-100">
                            {filteredRoles.length > 0 ? (
                                filteredRoles.map((rol) => (
                                    <tr key={rol.id} className="hover:bg-indigo-50 transition duration-100">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{rol.nombre}</td>
                                        <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">{rol.descripcion}</td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                                rol.nivel === 'Alto' ? 'bg-red-100 text-red-800' :
                                                rol.nivel === 'Medio' ? 'bg-yellow-100 text-yellow-800' :
                                                rol.nivel === 'Residente' ? 'bg-blue-100 text-blue-800' :
                                                'bg-green-100 text-green-800'
                                            }`}>
                                                {rol.nivel}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-center">
                                            {/* ÍCONO DE EDITAR - SquarePen */}
                                            <button 
                                                onClick={() => openModalForEdit(rol)}
                                                className="text-indigo-600 hover:text-indigo-900 mr-4 p-2 rounded-lg hover:bg-indigo-100 transition duration-150"
                                                title="Editar"
                                            >
                                                <SquarePen className="w-5 h-5 inline-block" />
                                            </button>
                                            {/* ÍCONO DE ELIMINAR - Trash2 */}
                                            <button 
                                                onClick={() => handleDelete(rol)} 
                                                className="text-red-600 hover:text-red-900 p-2 rounded-lg hover:bg-red-100 transition duration-150"
                                                title="Eliminar"
                                            >
                                                <Trash2 className="w-5 h-5 inline-block" />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="4" className="px-6 py-8 text-center text-gray-500 text-lg">
                                        No se encontraron roles que coincidan con la búsqueda.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            
            {/* ==================================================================== */}
            {/* MODAL PARA CREAR/EDITAR ROL */}
            {/* ==================================================================== */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-gray-900 bg-opacity-75 overflow-y-auto h-full w-full z-50 flex justify-center items-center p-4 backdrop-blur-sm">
                    <div className="bg-white p-6 sm:p-8 rounded-xl shadow-3xl w-full max-w-md transform transition-all duration-300 scale-100">
                        <div className="flex justify-between items-center mb-6 border-b pb-3">
                            <h2 className="text-2xl font-bold text-indigo-700 flex items-center">
                                {isEditing ? <SquarePen className="w-6 h-6 mr-2" /> : <Plus className="w-6 h-6 mr-2" />} 
                                {isEditing ? 'Editar Rol' : 'Registrar Nuevo Rol'}
                            </h2>
                            <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                                <X className="w-6 h-6" />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            {/* Campo Nombre */}
                            <div className="mb-4">
                                <label className="block text-gray-700 text-sm font-semibold mb-2">Nombre del Rol</label>
                                <input
                                    type="text"
                                    name="nombre"
                                    value={formData.nombre}
                                    onChange={handleChange}
                                    className="border border-gray-300 rounded-lg w-full py-2 px-4 text-gray-800 focus:ring-indigo-500 focus:border-indigo-500 transition duration-150"
                                    required
                                />
                            </div>
                            {/* Campo Descripción */}
                            <div className="mb-4">
                                <label className="block text-gray-700 text-sm font-semibold mb-2">Descripción</label>
                                <textarea
                                    name="descripcion"
                                    value={formData.descripcion}
                                    onChange={handleChange}
                                    className="border border-gray-300 rounded-lg w-full py-2 px-4 text-gray-800 h-20 resize-none focus:ring-indigo-500 focus:border-indigo-500 transition duration-150"
                                    required
                                />
                            </div>
                            {/* Selector de Nivel */}
                            <div className="mb-6">
                                <label className="block text-gray-700 text-sm font-semibold mb-2">Nivel</label>
                                <select
                                    name="nivel"
                                    value={formData.nivel}
                                    onChange={handleChange}
                                    className="border border-gray-300 rounded-lg w-full py-2 px-4 text-gray-800 focus:ring-indigo-500 focus:border-indigo-500 bg-white transition duration-150 appearance-none"
                                    required
                                >
                                    <option value="" disabled>Seleccione un Nivel</option>
                                    <option value="Alto">Alto</option>
                                    <option value="Medio">Medio</option>
                                    <option value="Bajo">Bajo</option>
                                    <option value="Residente">Residente</option>
                                </select>
                            </div>

                            {/* Botones */}
                            <div className="flex justify-end space-x-3">
                                <button 
                                    type="button" 
                                    onClick={closeModal}
                                    className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-2 px-4 rounded-xl transition duration-150 shadow-md"
                                >
                                    Cancelar
                                </button>
                                <button 
                                    type="submit"
                                    className="flex items-center bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 px-4 rounded-xl transition duration-150 shadow-md"
                                >
                                    <Save className="w-5 h-5 mr-2" />
                                    {isEditing ? 'Guardar Cambios' : 'Registrar Rol'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            
            {/* ==================================================================== */}
            {/* MODAL DE CONFIRMACIÓN DE ELIMINACIÓN */}
            {/* ==================================================================== */}
            {isConfirmModalOpen && roleToDelete && (
                <div className="fixed inset-0 bg-gray-900 bg-opacity-75 overflow-y-auto h-full w-full z-50 flex justify-center items-center p-4 backdrop-blur-sm">
                    <div className="bg-white p-6 rounded-xl shadow-3xl w-full max-w-sm transform transition-all duration-300">
                        <div className="text-center">
                            {/* ÍCONO DE ALERTA - AlertTriangle */}
                            <AlertTriangle className="mx-auto h-12 w-12 text-red-500" />
                            <h3 className="text-xl font-bold leading-6 text-gray-900 mt-4">Confirmar Eliminación</h3>
                            <div className="mt-2">
                                <p className="text-sm text-gray-500">
                                    ¿Estás seguro de que deseas eliminar el rol 
                                    <span className="font-semibold text-red-700"> "{roleToDelete.nombre}"</span>? Esta acción no se puede deshacer.
                                </p>
                            </div>
                        </div>
                        <div className="mt-5 sm:mt-6 flex justify-between space-x-3">
                            <button
                                type="button"
                                className="inline-flex justify-center rounded-xl border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:text-sm transition duration-150"
                                onClick={closeModal} // Usamos closeModal para resetear estados
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                className="inline-flex justify-center rounded-xl border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 sm:text-sm transition duration-150"
                                onClick={confirmDelete}
                            >
                                Eliminar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Roles;